import React, { memo } from "react";
import { Controller } from "react-hook-form";
import { useFormContext } from "./FormProvider";
import PropTypes from "prop-types";
import { RichTextEditor } from "../RichTextEditor/RichTextEditor";
import { createStyles } from "antd-style";

const useStyles = createStyles(({ css, token }) => ({
  editor: css`
    .quill {
      .ql-container {
        background-color: white;
        font-size: ${token.fontSize}px;
        font-family: ${token.fontFamily};
      }

      .ql-toolbar {
        border-top-left-radius: ${token.borderRadius}px;
        border-top-right-radius: ${token.borderRadius}px;
        background: ${token.colorBgContainer};
        border-color: ${token.colorBorder};
        
        .ql-picker-label {
          &:hover {
            color: ${token.colorPrimary};
          }
        }
        
        .ql-picker-options {
          background: ${token.colorBgContainer};
          border-color: ${token.colorBorder};
          box-shadow: ${token.boxShadow};
        }
        
        button {
          &:hover {
            color: ${token.colorPrimary};
            .ql-stroke {
              stroke: ${token.colorPrimary};
            }
            .ql-fill {
              fill: ${token.colorPrimary};
            }
          }
        }
      }

      .ql-container {
        border-bottom-left-radius: ${token.borderRadius}px;
        border-bottom-right-radius: ${token.borderRadius}px;
        border-color: ${token.colorBorder};
        
        .ql-editor {
          min-height: 150px;
          
          &.ql-blank::before {
            color: ${token.colorTextPlaceholder};
            font-style: normal;
          }
          
          h1, h2, h3, h4, h5, h6 {
            margin: 1rem 0;
          }
          
          blockquote {
            border-left: 4px solid ${token.colorBorder};
            padding-left: 1rem;
            margin: 1rem 0;
          }
          
          pre.ql-syntax {
            background-color: ${token.colorBgTextHover};
            color: ${token.colorText};
            overflow: visible;
            border-radius: ${token.borderRadius}px;
            padding: 1em;
            margin: 0.5em 0;
            font-family: ${token.fontFamilyCode};
            
            .hljs {
              background: transparent;
              padding: 0;
            }
            
            .hljs-keyword,
            .hljs-built_in {
              color: ${token.colorPrimary};
            }
            
            .hljs-string {
              color: ${token.colorSuccess};
            }
            
            .hljs-number {
              color: ${token.colorWarning};
            }
            
            .hljs-comment {
              color: ${token.colorTextDescription};
              font-style: italic;
            }
            
            .hljs-function {
              color: ${token.colorInfo};
            }
          }
        }
      }

      &.ql-error {
        .ql-toolbar,
        .ql-container {
          border-color: ${token.colorError};
        }
      }
      
      // Custom size styles
      .ql-snow .ql-size-small {
        font-size: 0.75em;
      }
      .ql-snow .ql-size-large {
        font-size: 1.5em;
      }
      .ql-snow .ql-size-huge {
        font-size: 2em;
      }
    }
  `,
}));

export const FormTextEditor = ({ 
  name, 
  helperText,
  ...props 
}) => {
  const { control, formConfig } = useFormContext();
  const { styles } = useStyles();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <div className={styles.editor}>
          <RichTextEditor
            {...field}
            {...formConfig}
            {...props}
            error={!!error}
            helperText={error?.message || helperText}
          />
        </div>
      )}
    />
  );
};

FormTextEditor.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  helperText: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  height: PropTypes.number,
  modules: PropTypes.object,
};

FormTextEditor.defaultProps = {
  helperText: "",
  disabled: false,
  required: false,
  height: 200,
};